/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import {
    ControlType,
    FieldUI,
    IFieldUI,
    IRule,
    SettingGroupDefinition
} from "@nuance/hierarchical-settings-ui-domain";
import { ILogger, getLogger } from "@nuance/hux-diagnostics";
import { CmdBuilder, IViewModel } from "@nuance/hap-components";
import { IRulePanelData, IRulePanelDataAppliedToSetting } from "../Panels/RulePanel/IRulePanelData";
import { IRulePanelProps } from "../Panels/RulePanel/IRulePanelProps";
import { IRulesGridState } from "./IRulesGridState";

const logger: ILogger = getLogger();

export class RulesGridCommands {
    constructor() {
        const cb = new CmdBuilder();
        const cmds = {
            onRuleSelectionChanged: cb.bindCmd(this.ruleSelectionChangedHandler),
            onAddRule: cb.bindCmdNP(this.addRuleHandler),
            onEditRule: cb.bindCmdNP(this.editRuleHandler),
            onSaveRulePanel: cb.bindCmd(this.saveRulePanelDataHandler),
            onCancelRulePanel: cb.bindCmdNP(this.cancelRulePanelHandler),
            onDeleteRule: cb.bindCmdNP(this.deleteRuleHandler)
        };

        this.Cmds = cmds;
        this.Reducer = cb.buildReducer<IRulesGridState>(cmds);
    }

    readonly Cmds;
    readonly Reducer;

    /////////////////////////////////////////////////////////////////
    // Command handlers
    /////////////////////////////////////////////////////////////////

    ruleSelectionChangedHandler = (state: IRulesGridState, ruleIndex: number) => {
        state.currentRuleIndex = ruleIndex;
        return state;
    };

    addRuleHandler = (state: IRulesGridState) => {
        logger.trackEvent("Add rule");

        const appliedTo = this.createAppliedToItems(state, undefined);

        const rulePanelProps: IRulePanelProps = {
            data: {
                isNew: true,
                ruleName: "",
                targetSettingName: "",
                regex: "",
                appliedToSettings: appliedTo
            },

            definition: state.definition,
            panelTitle: "Add New Rule",
            isOpen: true
        };

        state.currentRulePanelProps = rulePanelProps;
        state.isRulePanelOpen = true;
        return state;
    };

    editRuleHandler = (state: IRulesGridState): IRulesGridState => {
        if (state.currentRuleIndex !== -1) {
            const ruleToEdit = state.definition.UI.Rules[state.currentRuleIndex];
            logger.trackEvent("Edit rule", { name: ruleToEdit.RuleName });

            const appliedTo = this.createAppliedToItems(state, ruleToEdit);

            const rulePanelProps: IRulePanelProps = {
                data: {
                    isNew: false,
                    ruleName: ruleToEdit.RuleName,
                    targetSettingName: ruleToEdit.SettingName,
                    regex: ruleToEdit.RegexTemplate,
                    appliedToSettings: appliedTo
                },
                definition: state.definition,
                panelTitle: "Edit Rule",
                isOpen: true
            };

            state.currentRulePanelProps = rulePanelProps;
            state.isRulePanelOpen = true;
        } else {
            logger.logWarning("Attempting to edit rule but index is -1");
        }

        return state;
    };

    deleteRuleHandler = (state: IRulesGridState): IRulesGridState => {
        if (state.currentRuleIndex !== -1) {
            const rule = state.definition.UI.Rules[state.currentRuleIndex];
            const ruleName = rule.RuleName;
            logger.trackEvent("Delete rule", { name: ruleName });

            // remove rule from state
            state.definition.UI.Rules.splice(state.currentRuleIndex, 1);

            // clean up settings that use it.
            state.definition.UI.Controls.forEach(c => {
                const ui = c as FieldUI;

                if (ui.EnableRuleName === ruleName) {
                    ui.EnableRuleName = "";
                }
                if (ui.VisibleRuleName === ruleName) {
                    ui.VisibleRuleName = "";
                }
            });

            state.currentRuleIndex = -1;
            state.currentRulePanelProps = undefined;
        } else {
            logger.logWarning("Attempting to delete rule but index is -1");
        }

        return state;
    };

    private createAppliedToItems = (
        state: IRulesGridState,
        rule: IRule | undefined
    ): IRulePanelDataAppliedToSetting[] => {
        const appliedTo: IRulePanelDataAppliedToSetting[] = state.definition.UI.Controls?.filter(
            i =>
                i.ControlDiscriminator !== ControlType.Heading &&
                i.ControlDiscriminator !== ControlType.SectionHeading
        )?.map(c => {
            const uiCtrl = c as IFieldUI;

            let disabled: boolean = false;
            let hidden: boolean = false;
            let disabledIndeterminant: boolean = false;
            let hiddenIndeterminant: boolean = false;
            let hideInList: boolean = false;

            if (rule?.RuleName !== "" && uiCtrl.EnableRuleName === rule?.RuleName) {
                disabled = true;
            } else if (uiCtrl.EnableRuleName !== "") {
                disabledIndeterminant = true;
            }

            if (rule?.RuleName !== "" && uiCtrl.VisibleRuleName === rule?.RuleName) {
                hidden = true;
            } else if (uiCtrl.VisibleRuleName !== "") {
                hiddenIndeterminant = true;
            }

            if (rule?.SettingName === uiCtrl.SettingName) {
                hideInList = true;
            }

            const ap: IRulePanelDataAppliedToSetting = {
                settingName: uiCtrl.SettingName,
                isDisabledRuleApplied: disabled,
                isHiddenRuleApplied: hidden,
                isHiddenRuleAppliedIndeterminant: hiddenIndeterminant,
                isDisabledRuleAppliedIndeterminant: disabledIndeterminant,
                hideInList: hideInList
            };

            return ap;
        });

        return appliedTo;
    };

    saveRulePanelDataHandler = (state: IRulesGridState, panelData: IRulePanelData) => {
        logger.trackEvent("Save rule", {
            name: panelData.ruleName
        });

        const sg = new SettingGroupDefinition(state.definition);

        if (panelData.isNew) {
            //add
            const rule = sg.UI.AddRule(panelData.ruleName);
            rule.RegexTemplate = panelData.regex;
            rule.SettingName = panelData.targetSettingName;

            this.updateAppliedTo(panelData, state);
        } else {
            //update
            const rule = sg.UI.Rules.get(panelData.ruleName);
            if (rule === undefined) {
                logger.logError("Unable to find rule", { ruleName: panelData.ruleName });
                throw new Error("Unable to find rule.");
            }
            rule.RegexTemplate = panelData.regex;
            rule.SettingName = panelData.targetSettingName;

            this.updateAppliedTo(panelData, state);
        }

        state.definition = sg.toISettingGroupDefinition();
        state.isRulePanelOpen = false;
        state.currentRulePanelProps = undefined;

        return state;
    };

    private updateAppliedTo = (panelData: IRulePanelData, state: IRulesGridState): void => {
        // update the settings w the applied to rule.
        // spin thru the applied-to-setting list on the
        // panel data.  for each one, find the associated
        // UI.Control and set the rule name property

        panelData.appliedToSettings?.forEach(a => {
            // there should only be 1 control with a matching setting name...
            const uiCtrl = state.definition.UI.Controls?.find(c => {
                const ui = c as IFieldUI;
                if (ui.SettingName === a.settingName) {
                    return ui;
                }
            });

            if (uiCtrl) {
                const fieldUICtrl = uiCtrl as IFieldUI;
                fieldUICtrl.EnableRuleName = a.isDisabledRuleApplied
                    ? panelData.ruleName
                    : a.isDisabledRuleAppliedIndeterminant
                      ? fieldUICtrl.EnableRuleName
                      : "";
                fieldUICtrl.VisibleRuleName = a.isHiddenRuleApplied
                    ? panelData.ruleName
                    : a.isHiddenRuleAppliedIndeterminant
                      ? fieldUICtrl.VisibleRuleName
                      : "";
            }
        });
    };

    cancelRulePanelHandler = (state: IRulesGridState): IRulesGridState => {
        logger.trackEvent("Cancel editing rule", {
            ruleName: state.currentRulePanelProps?.data.ruleName
        });

        state.isRulePanelOpen = false;
        state.currentRulePanelProps = undefined;
        return state;
    };
}

type IRulesGridCmds = RulesGridCommands["Cmds"];

export type IRulesGridViewModel = IViewModel<IRulesGridState, IRulesGridCmds>;
